/**
 * API Type Definitions
 *
 * These types are placeholders pending OpenAPI/Swagger schema generation.
 * Once backend provides OpenAPI endpoint, regenerate with:
 * npx openapi-typescript {{OPENAPI_URL_TBD}} -o src/lib/types/api.ts
 */

// ============================================================================
// User Profile Types
// ============================================================================

export enum AccountStatus {
  Active = 'Active',
  Unverified = 'Unverified',
  Approved = 'Approved',
  Disabled = 'Disabled',
  Deleted = 'Deleted',
}

export interface AhpraRegistration {
  registrationNumber?: string;
  name?: string;
  primaryProfession?: string;
  location?: string;
  division?: string;
  registrationType?: string;
  postCode?: string;
  verificationStatus?: string;
}

export interface HealthCareRecord {
  professionId?: string;
  ahpraRecord?: AhpraRegistration;
}

export interface UserProfile {
  id: string;
  accountType: string;
  accountStatus: AccountStatus;

  // Name fields
  namePrefix?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  nameSuffix?: string;

  // Contact
  workPhoneNumber?: string;
  workEmailAddress?: string;

  // Profile details
  bio?: string;
  occupation?: string;
  dateOfBirth?: string; // ISO date string

  // Media
  image?: string; // S3 URL
  video?: string; // S3 URL
  animatedGesture?: string; // S3 URL

  // Organization
  memberOfOrganisation?: string[];
  pendingOrganisationMemberships?: PendingOrganisationMembership[];
  defaultOrganisation?: string;

  // Setup (GET /api/profile)
  needsSetup?: boolean;
  missingSetupItems?: string[];
  healthCareRecord?: HealthCareRecord;
  stripeAccount?: { id: string; abn?: string };
  marketingEmails?: boolean;
  onboardingComplete?: boolean;
  vanityUrl?: string;
  /** IANA name or the mobile app's fixed "UTC+10:00"; the API treats anything else as UTC. Own profile only. */
  timezone?: string | null;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

export interface UserRole {
  id: string;
  name: string;
}

export interface CreateProfileRequest {
  accountType: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  dateOfBirth?: string;
  marketingEmails?: boolean;
  bio?: string;
  workPhoneNumber?: string;
  workEmailAddress?: string;
}

export interface PendingOrganisationMembership {
  id: string;
  name: string;
}

export interface OrganisationSearchResult {
  id: string;
  type: string;
  name: string;
  bio: string;
  image: string;
  bannerImage: string;
}

export interface CreateOrganisationRequest {
  name: string;
  bio: string;
  type: string;
}

export interface CreateStripeConnectAccountRequest {
  bsb: string;
  accountNumber: string;
  abn: string;
  placeId: string;
}

export interface AbnVerificationResponse {
  abn: string;
  abnStatus: string;
  entityName: string;
  message: string;
}

export interface StripeConnectAccountResponse {
  id: string;
  payoutsEnabled: boolean;
  bankAccounts: { id: string; lastFour: string; defaultForCurrency?: boolean }[];
}

export interface HealthCareProfessionOption {
  id: string;
  name: string;
  description?: string;
  subspecialties?: HealthCareProfessionOption[];
}

export interface AvailabilityTimeSlot {
  isAvailable: boolean;
  availabilityType: string;
  fromTime: string;
  toTime: string;
}

export interface DayAvailability {
  day: string;
  availabilityTimes: AvailabilityTimeSlot[];
}

export interface UserAvailability {
  id: string;
  userId: string;
  patientDefaultLength: number;
  industryDefaultLength: number;
  weeklyAvailability: DayAvailability[];
}

export type ClaraSessionStatus = 'Draft' | 'Ingesting' | 'Generating' | 'Ready' | 'Active' | 'Completed' | 'Failed';

export type ClaraInputType = 'Url' | 'Pdf' | 'Text' | 'Curated' | 'Image';

export type ClaraContentReadingSource = 'WordCount' | 'Accreditation' | 'HcpConfirmed' | 'PartnerObserved';

export interface ClaraSession {
  id: string;
  status: ClaraSessionStatus;
  inputType: ClaraInputType;
  sourceTitle?: string;
  sourceSummary?: string;
  sourceUrl?: string | null;
  sourceFileName?: string | null;
  campaignId?: string;
  readingDurationMinutes?: number;
  contentWordCount?: number;
  contentReadingMinutes?: number;
  questionDurationMinutes?: number;
  contentReadingSource?: ClaraContentReadingSource;
  cpdAccreditationDetected?: boolean;
  cpdAccreditationConfidence?: 'high' | 'low' | null;
  cpdAccreditationBody?: string | null;
  cpdAccreditationActivityId?: string | null;
  cpdAccreditationStatedHours?: number | null;
  cpdAccreditationStatedPoints?: number | null;
  cpdAccreditationEvidenceExcerpt?: string | null;
  failureReason?: string;
  estimatedCoPay?: number;
  createdAt: string;
}

export interface ClaraSessionListItem {
  id: string;
  status: ClaraSessionStatus;
  /** `Curated` marks an Insights Session; absent on older APIs. */
  inputType?: ClaraInputType;
  sourceTitle?: string;
  campaignId?: string;
  questionCount?: number;
  estimatedCompletionSeconds?: number;
  estimatedCoPay?: number;
  createdAt: string;
  updatedAt: string;
}

export type ClaraSessionListFilter = 'all' | 'inProgress' | 'completed' | 'failed' | 'ready' | 'active' | 'curated';

export type ClaraSessionListPeriod = 'all' | '3m' | '6m' | '12m' | 'custom';

export interface ClaraSessionListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  filter?: ClaraSessionListFilter;
  /** Inclusive start date (YYYY-MM-DD). */
  from?: string;
  /** Inclusive end date (YYYY-MM-DD). */
  to?: string;
}

export interface ClaraSessionListPage {
  items: ClaraSessionListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ClaraCuratedCampaign {
  campaignId: string;
  organisationId: string;
  title: string;
  summary?: string;
  questionCount: number;
  estimatedCompletionSeconds: number;
  coPayPerCompletion: number;
  hasInProgressResponse: boolean;
  createdAt: string;
}

export interface ClaraCuratedCampaignList {
  items: ClaraCuratedCampaign[];
  total: number;
}

export interface ClaraReflectionStaffUser {
  userId: string;
  firstName: string;
  lastName: string;
  workEmailAddress?: string | null;
  occupation: string;
  professionId?: string | null;
  professionName?: string | null;
}

export interface ClaraReflectionStaffListItem {
  sessionId: string;
  status: ClaraSessionStatus;
  inputType: ClaraInputType;
  sourceTitle?: string | null;
  sourceUrl?: string | null;
  sourceFileName?: string | null;
  campaignId?: string | null;
  createdAt: string;
  updatedAt: string;
  generatedAt?: string | null;
  llmProvider?: string | null;
  llmModelId?: string | null;
  appliedRulesSnapshot?: string | null;
  user?: ClaraReflectionStaffUser | null;
  responseStatus?: string | null;
  responseCompletedAt?: string | null;
  questionCount?: number | null;
  estimatedCompletionSeconds?: number | null;
  coPayPerCompletion?: number | null;
  tags?: ClaraTag[];
}

export type ClaraReflectionStaffListFilter = ClaraSessionListFilter;

export type ClaraReflectionStaffResponseFilter = 'all' | 'none' | 'Started' | 'InComplete' | 'Complete' | 'Expired';

export interface ClaraReflectionStaffListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  filter?: ClaraReflectionStaffListFilter;
  inputType?: ClaraInputType | 'all';
  userId?: string;
  responseStatus?: ClaraReflectionStaffResponseFilter;
  /** Inclusive start date (YYYY-MM-DD). */
  from?: string;
  /** Inclusive end date (YYYY-MM-DD). */
  to?: string;
}

export interface ClaraReflectionStaffListPage {
  items: ClaraReflectionStaffListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ClaraReflectionReportTagTrend {
  label: string;
  category: string;
  count: number;
}

export interface ClaraReflectionReportRow {
  sessionId: string;
  status: ClaraSessionStatus;
  inputType: ClaraInputType;
  sourceTitle?: string | null;
  createdAt: string;
  responseStatus?: string | null;
  responseCompletedAt?: string | null;
  respondentLabel: string;
  userId?: string | null;
  userEmail?: string | null;
  professionName?: string | null;
  tags: ClaraTag[];
  answers: ClaraReflectionStaffAnswer[];
  qaSummary: string;
}

export interface ClaraReflectionReport {
  totalReflections: number;
  completedReflections: number;
  includeIdentity: boolean;
  deIdentificationMinCount: number;
  tagTrends: ClaraReflectionReportTagTrend[];
  rows: ClaraReflectionReportRow[];
}

export interface ClaraReflectionReportParams {
  search?: string;
  filter?: ClaraReflectionStaffListFilter;
  inputType?: ClaraInputType | 'all';
  userId?: string;
  responseStatus?: ClaraReflectionStaffResponseFilter;
  from?: string;
  to?: string;
  includeIdentity?: boolean;
  previewLimit?: number;
}

export type ClaraAnalyticsPeriod = '3m' | '6m' | '12m' | 'custom';

export interface ClaraPlatformAnalyticsPeriod {
  preset: ClaraAnalyticsPeriod | string;
  from: string;
  to: string;
}

export interface ClaraPlatformAnalyticsKpis {
  totalReflections: number;
  completedReflections: number;
  totalEarnAud: number;
  totalCoPayEarnedAud: number;
  totalMarketResearchEarnedAud: number;
  totalCpdHours: number;
}

export interface ClaraPlatformAnalyticsProfessionRow {
  profession: string;
  professionSlug: string;
  count: number;
  completionPercent: number;
  avgDurationMinutes: number;
  totalEarnAud: number;
  coPayEarnedAud: number;
  marketResearchEarnedAud: number;
}

export interface ClaraPlatformAnalyticsDailyCount {
  date: string;
  count: number;
}

export interface ClaraPlatformAnalyticsHourlyCount {
  hour: number;
  count: number;
}

export interface ClaraPlatformAnalyticsCohort {
  newUsers: number;
  returningUsers: number;
  totalUsers: number;
}

export interface ClaraPlatformAnalyticsTextLengthBucket {
  label: string;
  count: number;
}

export interface ClaraPlatformAnalyticsLlmModelRow {
  model: string;
  reflections: number;
  promptTokens: number;
  completionTokens: number;
  spendUsd: number;
}

export interface ClaraPlatformAnalyticsLlmPurposeBucket {
  purpose: string;
  label: string;
  calls: number;
  promptTokens: number;
  completionTokens: number;
  spendUsd: number;
}

export interface ClaraPlatformAnalyticsLlmCost {
  currency: string;
  totalSpendUsd: number;
  perReflectionUsd: number;
  generationTokens: number;
  summaryTokens: number;
  dataAvailable: boolean;
  byPurpose: ClaraPlatformAnalyticsLlmPurposeBucket[];
  byModel: ClaraPlatformAnalyticsLlmModelRow[];
}

export interface ClaraPlatformAnalytics {
  period: ClaraPlatformAnalyticsPeriod;
  kpis: ClaraPlatformAnalyticsKpis;
  deIdentificationMinCount: number;
  byProfession: ClaraPlatformAnalyticsProfessionRow[];
  reflectionsPerDay: ClaraPlatformAnalyticsDailyCount[];
  reflectionsByHourUtc: ClaraPlatformAnalyticsHourlyCount[];
  newVsReturning: ClaraPlatformAnalyticsCohort;
  freeTextLengthBuckets: ClaraPlatformAnalyticsTextLengthBucket[];
  llmCost: ClaraPlatformAnalyticsLlmCost;
}

export interface ClaraPlatformAnalyticsParams {
  filter?: ClaraReflectionStaffListFilter;
  inputType?: ClaraInputType | 'all';
  responseStatus?: ClaraReflectionStaffResponseFilter;
  from?: string;
  to?: string;
  period?: ClaraAnalyticsPeriod;
}

export interface ClaraReflectionStaffAnswer {
  questionId: string;
  questionType: string;
  questionText: string;
  answer?: CampaignAnswer | null;
}

export interface ClaraReflectionStaffDetail {
  sessionId: string;
  status: ClaraSessionStatus;
  inputType: ClaraInputType;
  sourceTitle?: string | null;
  sourceSummary?: string | null;
  sourceUrl?: string | null;
  sourceFileName?: string | null;
  generationContentExcerpt?: string | null;
  campaignId?: string | null;
  readingDurationMinutes: number;
  failureReason?: string | null;
  createdAt: string;
  updatedAt: string;
  generatedAt?: string | null;
  llmProvider?: string | null;
  llmModelId?: string | null;
  appliedRulesSnapshot?: string | null;
  generationSystemPrompt?: string | null;
  generationUserPrompt?: string | null;
  user?: ClaraReflectionStaffUser | null;
  campaign?: Campaign | null;
  responseId?: string | null;
  responseStatus?: string | null;
  responseStartedAt?: string | null;
  responseCompletedAt?: string | null;
  answers: ClaraReflectionStaffAnswer[];
}

export interface ClaraResolvedRulesLayer {
  scope: string;
  scopeId?: string | null;
  scopeLabel?: string | null;
  revisionNumber?: number | null;
  version?: string | null;
  source: string;
  available: boolean;
  message?: string | null;
  rules?: Record<string, unknown> | null;
}

export interface ClaraResolvedRulesReference {
  rulesRef: string;
  layers: ClaraResolvedRulesLayer[];
}

export interface CreateClaraSessionRequest {
  inputType: ClaraInputType;
  url?: string;
  text?: string;
  /**
   * A partner embed capture to build the session from. When set, the server takes the canonical
   * URL and the already-extracted article text from the capture and ignores url/text/inputType.
   */
  captureId?: string;
  /** Article text read from the page itself, sent alongside `url`. See the extension's path. */
  extractedText?: string;
  /** Title read from the page, used in place of a hostname. */
  extractedTitle?: string;
}

export interface UpdateClaraReadingTimeRequest {
  contentReadingMinutes: number;
}

export interface ConfirmClaraSessionResponse {
  session: ClaraSession;
  campaignId: string;
  responseId: string;
}

export interface ClaraCpdReflection {
  question: string;
  answer: string;
}

export interface ClaraCpdRecord {
  sessionId: string;
  practitionerName: string;
  medNumber: string;
  profession: string;
  healthProfession?: string;
  specialtyScope?: string;
  activityTitle: string;
  activitySummary?: string;
  completedAt: string;
  recordGeneratedAt?: string;
  readingDurationMinutes: number;
  contentWordCount?: number;
  contentReadingMinutes?: number;
  questionDurationMinutes?: number;
  contentReadingSource?: ClaraContentReadingSource;
  cpdAccreditationDetected?: boolean;
  cpdAccreditationConfidence?: 'high' | 'low' | null;
  cpdAccreditationBody?: string | null;
  cpdAccreditationActivityId?: string | null;
  cpdAccreditationStatedHours?: number | null;
  cpdAccreditationEvidenceExcerpt?: string | null;
  cpdHours: number;
  sessionContentSummary?: string;
  keyLearnings?: string[];
  reflectionSummary?: string;
  reflections: ClaraCpdReflection[];
}

export type EngagementSuggestionType =
  | 'Campaign'
  | 'InCompleteCampaign'
  | 'Organisation'
  | 'Individual'
  | 'NewsArticle'
  | 'FullScreenVideo'
  | 'FullScreenImage'
  | 'QuickInsight';

export type EngagementSuggestionStatus = 'Unread' | 'Read' | 'Declined' | 'Complete';

export interface EngagementSuggestion {
  id: string;
  type: EngagementSuggestionType;
  createdAt: string;
  userStatus?: EngagementSuggestionStatus;
  organisationId?: string;
  profileId?: string;
  professionId?: string;
  campaignId?: string;
  postId?: string;
  name?: string;
  title?: string;
  summary?: string;
  subtitle?: string;
  imageUrl?: string;
  source?: string;
  publishedAt?: string;
  coPayPerCompletion?: number;
  marketResearchPerCompletion?: number;
  questionCount?: number;
  nextQuestionIndex?: number;
  estimatedCompletionSeconds?: number;
  estimatedCompletionTime?: number;
  campaignOrigin?: string;
  claraSessionId?: string;
  tags?: string[];
}

/** Campaign-type item from GET /api/engagement-suggestions (e.g. STAT Signal surfacing). */
export interface EngagementSuggestionCampaign {
  id: string;
  type: 'Campaign';
  createdAt: string;
  campaignId: string;
  organisationId: string;
  title: string;
  summary?: string | null;
  subtitle?: string | null;
  coPayPerCompletion?: number | null;
  marketResearchPerCompletion?: number | null;
  questionCount?: number | null;
  estimatedCompletionSeconds?: number | null;
  estimatedCompletionTime?: number | null;
  campaignOrigin?: string | null;
  tags?: string[] | null;
}

export type ClaraAllowanceWindow = 'Day' | 'Week' | 'Month' | 'Year';

export type Weekday = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

export interface ClaraReflectionAllowanceSettings {
  window: ClaraAllowanceWindow;
  maxReflections: number;
  refreshDayOfWeek: Weekday;
  refreshDayOfMonth: number;
  refreshMonth: number;
  refreshHour: number;
  activeDayBonusEnabled: boolean;
  activeDayTarget: number;
  bonusReflections: number;
}

export interface ClaraSystemSettings {
  enabled: boolean;
  maxGenerationsPerUserPerDay: number;
  reflectionAllowance?: ClaraReflectionAllowanceSettings;
  requireCampaignApproval?: boolean;
  fundingOrganisationId?: string;
  deIdentificationMinCount?: number;
  urlBlacklist?: string[];
  minShortAnswerWords?: number;
  minLongAnswerWords?: number;
  minShortAnswerChars?: number;
  minLongAnswerChars?: number;
  answerDenyList?: string[];
  cpdReadingWordsPerMinute?: number;
  cpdAccreditationDetectionEnabled?: boolean;
  cpdAccreditationDetectionSystemPrompt?: string;
  cpdAccreditationDetectionTemperature?: number;
  cpdAccreditationDetectionMaxTokens?: number;
}

export type ClaraCoPayPricingMode = 'Fmv' | 'FixedPerQuestionType';

export interface ClaraManagementSettings {
  enabled: boolean;
  maxGenerationsPerUserPerDay: number;
  reflectionAllowance?: ClaraReflectionAllowanceSettings;
  requireCampaignApproval: boolean;
  fundingOrganisationId: string;
  deIdentificationMinCount: number;
  urlBlacklist: string[];
  coPayPricingMode: ClaraCoPayPricingMode;
  coPayByQuestionType: Record<string, number>;
  duplicateDetectionEnabled?: boolean;
  duplicateGrayZoneAdjudicationEnabled?: boolean;
  duplicateEmbeddingSimilarityBlock?: number;
  duplicateEmbeddingSimilarityReview?: number;
  duplicateLookbackSessions?: number;
  duplicateLookbackDays?: number;
  contentEligibilityEnabled?: boolean;
  contentEligibilityMinConfidence?: number;
  answerQualityValidationEnabled?: boolean;
  answerQualitySystemPrompt?: string;
  answerQualityTemperature?: number;
  answerQualityMaxTokens?: number;
  minShortAnswerWords?: number;
  minLongAnswerWords?: number;
  minShortAnswerChars?: number;
  minLongAnswerChars?: number;
  answerDenyList?: string[];
  cpdReadingWordsPerMinute?: number;
  cpdAccreditationDetectionEnabled?: boolean;
  cpdAccreditationDetectionSystemPrompt?: string;
  cpdAccreditationDetectionTemperature?: number;
  cpdAccreditationDetectionMaxTokens?: number;
}

export interface UpsertClaraManagementSettingsRequest {
  enabled: boolean;
  maxGenerationsPerUserPerDay: number;
  /** Left out, the API keeps the stored allowance. */
  reflectionAllowance?: ClaraReflectionAllowanceSettings;
  requireCampaignApproval: boolean;
  fundingOrganisationId: string;
  deIdentificationMinCount: number;
  urlBlacklist: string[];
  coPayPricingMode: ClaraCoPayPricingMode;
  coPayByQuestionType: Record<string, number>;
  duplicateDetectionEnabled?: boolean;
  duplicateGrayZoneAdjudicationEnabled?: boolean;
  duplicateEmbeddingSimilarityBlock?: number;
  duplicateEmbeddingSimilarityReview?: number;
  duplicateLookbackSessions?: number;
  duplicateLookbackDays?: number;
  contentEligibilityEnabled?: boolean;
  contentEligibilityMinConfidence?: number;
  answerQualityValidationEnabled?: boolean;
  answerQualitySystemPrompt?: string;
  answerQualityTemperature?: number;
  answerQualityMaxTokens?: number;
  minShortAnswerWords?: number;
  minLongAnswerWords?: number;
  minShortAnswerChars?: number;
  minLongAnswerChars?: number;
  answerDenyList?: string[];
  cpdReadingWordsPerMinute?: number;
  cpdAccreditationDetectionEnabled?: boolean;
  cpdAccreditationDetectionSystemPrompt?: string;
  cpdAccreditationDetectionTemperature?: number;
  cpdAccreditationDetectionMaxTokens?: number;
  includesAnswerValidation?: boolean;
}

export interface ClaraRules {
  version: string;
  systemPrompt: string;
  questionGuidelines: string;
  safetyRules: string;
  taggingGuidelines: string;
  campaignCreationSummaryPrompt: string;
  campaignCreationQuestionPrompt: string;
  aiCampaignGenerationPrompt: string;
  aiCampaignGenerationMinQuestions: number;
  aiCampaignGenerationMaxQuestions: number;
  aiCampaignGenerationDefaultMaxSpend: number;
  aiCampaignQuestionWeightShortAnswer: number;
  aiCampaignQuestionWeightLongAnswer: number;
  aiCampaignQuestionWeightMultiSelection: number;
  aiCampaignQuestionWeightDropdown: number;
  aiCampaignQuestionWeightTrueFalse: number;
  aiCampaignQuestionWeightSlidingScale: number;
  maxQuestions: number;
  revisionNumber: number;
  updatedAt: string;
  /** False when the API skipped archiving because the revision table is missing. */
  revisionHistoryAvailable?: boolean;
  /** True when this save archived the previous rules (upsert responses only). */
  revisionArchived?: boolean;
}

export interface UpsertClaraRulesRequest {
  version: string;
  systemPrompt: string;
  questionGuidelines: string;
  safetyRules: string;
  taggingGuidelines: string;
  campaignCreationSummaryPrompt: string;
  campaignCreationQuestionPrompt: string;
  aiCampaignGenerationPrompt: string;
  aiCampaignGenerationMinQuestions: number;
  aiCampaignGenerationMaxQuestions: number;
  aiCampaignGenerationDefaultMaxSpend: number;
  aiCampaignQuestionWeightShortAnswer: number;
  aiCampaignQuestionWeightLongAnswer: number;
  aiCampaignQuestionWeightMultiSelection: number;
  aiCampaignQuestionWeightDropdown: number;
  aiCampaignQuestionWeightTrueFalse: number;
  aiCampaignQuestionWeightSlidingScale: number;
  maxQuestions: number;
}

export interface GenerateCampaignSummaryRequest {
  draftSummary: string;
  campaignTitle?: string;
  campaignGoal?: string;
}

export interface GenerateCampaignSummaryResponse {
  summary: string;
}

export interface GenerateCampaignQuestionRequest {
  draftQuestion: string;
  questionType: string;
  campaignTitle?: string;
  campaignGoal?: string;
  campaignSummary?: string;
}

export interface GenerateCampaignQuestionResponse {
  question: string;
}

export interface GenerateOrganisationCampaignFromAiRequest {
  sourceText: string;
}

export interface ClaraProfessionRules {
  professionId: string;
  professionName: string;
  enabled: boolean;
  additionalSystemPrompt?: string | null;
  additionalQuestionGuidelines?: string | null;
  additionalSafetyRules?: string | null;
  avoidTopics: string[];
  maxQuestionsOverride?: number | null;
  revisionNumber?: number;
  updatedAt?: string;
}

export interface UpsertClaraProfessionRulesRequest {
  enabled: boolean;
  additionalSystemPrompt?: string | null;
  additionalQuestionGuidelines?: string | null;
  additionalSafetyRules?: string | null;
  avoidTopics: string[];
  maxQuestionsOverride?: number | null;
}

export interface ClaraUserAiRules {
  userId: string;
  enabled: boolean;
  additionalInstructions?: string | null;
  additionalSystemPrompt?: string | null;
  additionalQuestionGuidelines?: string | null;
  additionalSafetyRules?: string | null;
  avoidTopics: string[];
  adminNotes?: string | null;
  revisionNumber?: number;
  updatedAt?: string;
}

export interface UpsertClaraUserAiRulesRequest {
  enabled: boolean;
  additionalInstructions?: string | null;
  additionalSystemPrompt?: string | null;
  additionalQuestionGuidelines?: string | null;
  additionalSafetyRules?: string | null;
  avoidTopics: string[];
  adminNotes?: string | null;
}

export interface PreviewClaraGlobalRulesDraft {
  version?: string;
  systemPrompt?: string;
  questionGuidelines?: string;
  safetyRules?: string;
  taggingGuidelines?: string;
  maxQuestions?: number;
}

export interface PreviewClaraProfessionRulesDraft {
  professionId: string;
  enabled?: boolean;
  additionalSystemPrompt?: string | null;
  additionalQuestionGuidelines?: string | null;
  additionalSafetyRules?: string | null;
  avoidTopics?: string[];
  maxQuestionsOverride?: number | null;
}

export interface PreviewClaraUserRulesDraft {
  userId: string;
  enabled?: boolean;
  additionalInstructions?: string | null;
  additionalSystemPrompt?: string | null;
  additionalQuestionGuidelines?: string | null;
  additionalSafetyRules?: string | null;
  avoidTopics?: string[];
}

export interface PreviewClaraRulesRequest {
  global?: PreviewClaraGlobalRulesDraft;
  profession?: PreviewClaraProfessionRulesDraft;
  user?: PreviewClaraUserRulesDraft;
  practitionerProfessionName?: string;
  title?: string;
  summary?: string;
  excerpt?: string;
}

export interface ClaraRulesPreview {
  systemPrompt: string;
  userPrompt: string;
  taggingSystemPrompt: string;
  taggingUserPrompt: string;
  effectiveMaxQuestions: number;
  rulesSnapshot: string;
}

export interface ClaraRulesRevisionSummary {
  id: string;
  scope: string;
  scopeId?: string | null;
  scopeLabel?: string | null;
  revisionNumber: number;
  createdAt: string;
}

export interface ClaraRulesRevisionListPage {
  items: ClaraRulesRevisionSummary[];
  page: number;
  pageSize: number;
  total: number;
  /** False when the revision table has not been created (run migrate-db). */
  historyAvailable?: boolean;
}

export interface ClaraRulesRevisionDetail extends ClaraRulesRevisionSummary {
  payload?: unknown;
}

// ============================================================================
// Practitioner Verification Types
// ============================================================================

export interface PendingPractitioner {
  id: string;
  namePrefix?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  nameSuffix?: string;
  accountType: string;
  workPhoneNumber?: string;
  workEmailAddress?: string;
  accountStatus: AccountStatus;
  image?: string; // Signed S3 URL for profile image
  identityDocumentUrls: string[]; // Array of signed S3 URLs

  // Additional practitioner details
  qualifications?: string; // Professional qualifications/degrees
  workAddress?: string; // Professional address
  createdAt?: string; // ISO date string
  specialization?: string; // Medical specialization
  yearsOfExperience?: number; // Years of practice
  licenseNumber?: string; // Professional license number
  bio?: string; // Professional biography
  website?: string; // Professional website URL
  linkedinUrl?: string; // LinkedIn profile URL

  // Onboarding & professional detail — surfaced for verification triage
  occupation?: string;
  dateOfBirth?: string; // ISO date string
  onboardingComplete?: boolean;

  // AHPRA registration — healthCareRecord.ahpraRecord holds the registration
  // number, primary profession, division and verification status.
  healthCareRecord?: HealthCareRecord;

  // Profession hierarchy resolved server-side from healthCareRecord.professionId
  // (the AHPRA record's own profession fields are frequently null).
  primaryProfession?: string;
  secondaryProfession?: string;
  specialty?: string;

  // Bank / payout onboarding status (Stripe Connect account created during setup)
  bankAccountSetup?: boolean;
  abn?: string; // ABN captured during banking setup
  abnEntityName?: string; // Registered entity name from the ABR lookup
}

export interface VerifyPractitionerRequest {
  profileId: string;
  approved: boolean;
  // TBD: Add additional fields once backend schema is confirmed
  // notes?: string;
  // rejectionReason?: string;
}

export interface VerifyPractitionerResponse {
  success: boolean;
  message?: string;
  // TBD: Add response fields once backend schema is confirmed
}

// ============================================================================
// API Response Wrapper
// ============================================================================

export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers?: Record<string, string>;
}

export interface ApiError {
  message: string;
  code?: string;
  status: number;
  details?: unknown;
}

// ============================================================================
// Configuration Types
// ============================================================================

export interface AppConfig {
  environment: 'development' | 'staging' | 'production';
  api: {
    host: string;
    timeout: number;
  };
  auth: {
    user_pool_client_id: string;
    client_id: string;
    user_pool_id: string;
    aws_region: string;
    domain: string;
    identity_pool_id: string;
    /**
     * Cognito hosted-UI settings for social sign-in. Absent until SSO is
     * deployed for the environment. Snake_case mirrors the Amplify Gen 2
     * outputs schema so web and mobile configs stay symmetric.
     */
    oauth?: {
      domain: string;
      scopes: string[];
      redirect_sign_in_uri: string[];
      redirect_sign_out_uri: string[];
      response_type: 'code';
      identity_providers: string[];
    };
  };
  build: {
    number: string;
    timestamp: string;
    commit: string;
  };
  features: {
    blog: boolean;
    analytics: boolean;
  };
  /** Optional — enables Google Places address autocomplete in onboarding */
  googleMaps?: {
    apiKey?: string;
  };
  /**
   * Optional — Firebase web app credentials that enable push notifications for
   * chat. Values come from Firebase console → Project settings → Your apps (Web);
   * `vapidKey` is the Web Push certificate key pair under Cloud Messaging.
   * Placeholder values (e.g. `{{FIREBASE_WEB_API_KEY_TBD}}`) are treated as absent.
   */
  firebase?: {
    apiKey?: string;
    authDomain?: string;
    projectId?: string;
    storageBucket?: string;
    messagingSenderId?: string;
    appId?: string;
    vapidKey?: string;
  };
  /** Stripe publishable key for payment method setup */
  stripe?: {
    publishable_key?: string;
    merchant_identifier?: string;
    url_scheme?: string;
  };
  /** Sentry error monitoring configuration */
  sentry: {
    /** Sentry DSN (Data Source Name) URL */
    dsn: string;
    /** Deployment environment (development, staging, production) */
    environment: 'development' | 'staging' | 'production';
    /** Whether Sentry is enabled for this environment */
    enabled: boolean;
    /** Percentage of transactions to send (0.0 to 1.0) */
    tracesSampleRate: number;
    /** Percentage of sessions to record (0.0 to 1.0) */
    replaysSessionSampleRate: number;
    /** Percentage of error sessions to record (0.0 to 1.0) */
    replaysOnErrorSampleRate: number;
  };
}

// ============================================================================
// Cognito Types (from aws-amplify)
// ============================================================================

export interface CognitoUser {
  username: string;
  userId: string;
  signInDetails?: {
    loginId?: string;
  };
}

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
}

export interface CognitoUserAttributes {
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  'custom:role'?: string;
  // Cognito groups are typically in a separate claim
}

// ============================================================================
// Auth Store Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  name?: string;
  avatar?: string;
  image?: string; // Profile image URL (same as avatar, used interchangeably)
  role?: 'admin' | 'super_admin' | 'user';
  groups?: string[]; // Cognito groups
  attributes?: CognitoUserAttributes;
}

export interface AuthState {
  user: User | null;
  idToken: string | null; // ID Token for API authentication
  loading: boolean;
  error: string | null;
  isAuthReady: boolean; // Whether auth initialization has completed (success or failure)
}

// ============================================================================
// Consultation Types
// ============================================================================

export enum MeetingTypes {
  onlineVideo = 'onlineVideo',
  inPerson = 'inPerson',
}

export enum ConsultationTypes {
  healthCareMeeting = 'healthCareMeeting',
  industryLeadMeeting = 'industryLeadMeeting',
  marketResearch = 'marketResearch',
}

export enum ConsultationStatus {
  canceled = 'canceled',
  active = 'active',
  started = 'started',
  complete = 'complete',
  invalidHcpAttendance = 'invalidHcpAttendance',
  callEnded = 'callEnded',
}

export enum TransactionStatuses {
  unpaid = 'unpaid',
  paid = 'paid',
  preauth_success = 'preauth_success',
  preauth_failure = 'preauth_failure',
}

export enum TransactionType {
  credit = 'credit',
  debit = 'debit',
  preauth = 'preauth',
}

export enum PaymentMethod {
  card = 'card',
  bankAccount = 'bankAccount',
  copay = 'copay',
  connectAccount = 'connectAccount',
}

export enum AttendeeStatus {
  pending = 'pending',
  attending = 'attending',
  declined = 'declined',
  unapproved = 'unapproved',
}

export enum MarketResearchConsultStatus {
  created = 'created',
  awaitingData = 'awaitingData',
  awaitingApproval = 'awaitingApproval',
  sentToPractitioner = 'sentToPractitioner',
  approved = 'approved',
}

export enum TransferStatus {
  succeeded = 'succeeded',
  failed = 'failed',
}

export interface Consultation {
  id: string;
  ownerProfileId: string;
  ownerCompanyId?: string;
  startTime: string; // ISO date string
  endTime: string; // ISO date string
  createdAt: string; // ISO date string
  status: ConsultationStatus;
  consultationType: ConsultationTypes;
  details: string;
  attendees: ConsultationAttendee[];
  /** Present on organisation consultation list/detail when API enriches profiles */
  ownerDisplayName?: string;
  attendeeDisplayNames?: string[];
  meeting?: Meeting;
  transactionData?: TransactionData;
  marketResearchData?: MarketResearchData;
  stepFunctionsTaskToken?: string;
}

export interface UserAvailabilityTimeSlot {
  userId: string;
  startDateTime: string;
  endDateTime: string;
  type: string;
  totalCost: number;
  taxPercentage: number;
  taxAmount: number;
}

export interface GetAvailableTimeSlotsRequest {
  userId: string;
  startDate: string;
  endDate: string;
  type: string;
}

export interface CreateConsultationRequest {
  id: string;
  ownerProfileId: string;
  ownerCompanyId?: string;
  startTime: string;
  endTime: string;
  status: string;
  details: string;
  consultationType: string;
  attendees: { id: string; status?: AttendeeStatus }[];
  meeting?: {
    meetingType: MeetingTypes;
    inPersonAddress?: string;
  };
  sourceSignalMatchId?: string;
}

/** Minimal HCP identity for opening the schedule-consultation flow from claimed leads. */
export interface ScheduleConsultationLead {
  userId: string;
  displayName: string;
  professionName?: string;
  occupation?: string;
  signalMatchId?: string;
}

/** Paginated response from GET /api/organisation/{id}/consultations */
export interface OrganisationConsultationsPage {
  items: Consultation[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ConsultationAttendee {
  id: string;
  status?: AttendeeStatus;
  review?: ConsultationReview;
}

export interface ConsultationReview {
  overallRating: number;
  callProblem: boolean;
  callProblemComments?: string;
  additionalComments?: string;
}

export interface Meeting {
  meetingType: MeetingTypes;
  startTime?: string; // ISO date string
  endTime?: string; // ISO date string
  sessionDuration?: string; // ISO duration string
  sessionId?: string;
  meetingAttendees?: MeetingAttendee[];
  inPersonAddress?: string;
  timelineEvents?: TimelineEvent[];
}

export interface MeetingAttendee {
  id: string;
  name: string;
  joinTime: string; // ISO date string
  leaveTime?: string; // ISO date string
  leaveReason?: string;
}

export interface TimelineEvent {
  userId: string;
  userName: string;
  eventKind: string;
  timestamp: string; // ISO date string
  description?: string;
  additionalData?: string;
}

export interface TransactionData {
  costComponents: CostComponents;
  transactionStatus: TransactionStatuses;
  preauthTransaction?: ConsultationTransaction;
  consultationTransactions?: ConsultationTransaction[];
  connectAccountTransfers?: ConnectAccountTransfer[];
}

export interface CostComponents {
  totalCost: number;
  subTotal: number;
  serviceFee: number;
  practitionerServiceFee: number;
  statCoPayAllocation: number;
  healthCarePaymentTax: number;
  healthCareCoPayAllocation: number;
  healthCarePaymentAllocation: number;
  healthCarePaymentTotal: number;
  taxPercentage: number;
  taxAmount: number;
}

export interface ConsultationTransaction {
  id: string;
  status: string;
  paymentMethod: PaymentMethod;
  transactionType: TransactionType;
  createdAt: string; // ISO date string
  failureReason?: string;
  amountAttempted?: number;
  amountCaptured?: number;
  latestChargeId?: string;
  latestBalanceTxnId?: string;
  balanceAvailableOn?: string; // ISO date string
}

export interface ConnectAccountTransfer {
  id: string;
  status: TransferStatus;
  currency: string;
  amount: number;
  createdAt: string; // ISO date string
  description?: string;
  destination?: string;
  failureReason?: string;
  reversed?: boolean;
  balanceTransaction?: string;
}

export interface MarketResearchData {
  marketResearchConsultStatus: MarketResearchConsultStatus;
  consultationTopic: string;
  agreementDocument?: S3Blob;
  userAgreements?: UserAgreement[];
}

export interface S3Blob {
  bucket: string;
  key: string;
  url?: string;
}

export interface UserAgreement {
  userId: string;
  hasAgreed: boolean;
  agreedAt: string; // ISO date string
  agreementSignature?: S3Blob;
}

// ============================================================================
// Campaign Types
// ============================================================================

export type CampaignStatus =
  | 'Draft'
  | 'PendingApproval'
  | 'Approved'
  | 'Running'
  | 'Declined'
  | 'Paused'
  | 'Ended'
  | 'Deleted';

export type CampaignQuestionType =
  | 'ShortAnswer'
  | 'LongAnswer'
  | 'MultiSelection'
  | 'Dropdown'
  | 'TrueFalse'
  | 'SlidingScale';

export type CampaignUserStatus = 'Partial' | 'Completed';

export interface CampaignUser {
  userId: string;
  status: CampaignUserStatus;
  completedAt?: string | null;
}

export interface CampaignQuestionBase {
  id: string;
  type: CampaignQuestionType;
  text: string;
  helperText?: string | null;
}

export interface ShortAnswerQuestion extends CampaignQuestionBase {
  type: 'ShortAnswer';
  maxWords: number;
}

export interface LongAnswerQuestion extends CampaignQuestionBase {
  type: 'LongAnswer';
  maxWords: number;
}

export interface MultiSelectionQuestion extends CampaignQuestionBase {
  type: 'MultiSelection';
  options: string[];
}

export interface DropdownQuestion extends CampaignQuestionBase {
  type: 'Dropdown';
  options: string[];
}

export interface TrueFalseQuestion extends CampaignQuestionBase {
  type: 'TrueFalse';
}

export interface SlidingScaleQuestion extends CampaignQuestionBase {
  type: 'SlidingScale';
  min: number;
  max: number;
  step: number;
}

export type CampaignQuestion =
  | ShortAnswerQuestion
  | LongAnswerQuestion
  | MultiSelectionQuestion
  | DropdownQuestion
  | TrueFalseQuestion
  | SlidingScaleQuestion;

export type CampaignOrigin = 'Organisation' | 'Clara';

export type ClaraTagCategory =
  | 'Condition'
  | 'Symptom'
  | 'Medication'
  | 'Ingredient'
  | 'TherapeuticArea'
  | 'Company'
  | 'Other';

export interface ClaraTag {
  label: string;
  category: ClaraTagCategory;
  confidence: number;
}

export interface Campaign {
  id: string;
  organisationId: string;
  origin?: CampaignOrigin;
  claraSessionId?: string;
  targetUserId?: string;
  status: CampaignStatus;
  campaignName: string;
  title: string;
  goal: string;
  summary: string;
  maxSpend: number;
  coPayPerCompletion: number;
  spend: number;
  /** Remaining campaign wallet balance (same as “Remaining” in the campaigns list). */
  availableFunds?: number;
  /** Total estimated completion duration in seconds (from API). */
  estimatedCompletionTime?: number | null;
  declineReason?: string | null;
  questions: CampaignQuestion[];
  users: CampaignUser[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  approvedBy?: string | null;
  approvedAt?: string | null;
  pausedAt?: string | null;
  filters?: CampaignFilters | null;
  primaryProfessionCosts?: CampaignPrimaryProfessionCost[];
  tags?: ClaraTag[];
}

export interface CampaignProfessionCost {
  professionId: string;
  professionName: string;
  averageCostPerCompletion: number;
}

export type CampaignQuestionCreateInput =
  | (Omit<ShortAnswerQuestion, 'id'> & { id?: string })
  | (Omit<LongAnswerQuestion, 'id'> & { id?: string })
  | (Omit<MultiSelectionQuestion, 'id'> & { id?: string })
  | (Omit<DropdownQuestion, 'id'> & { id?: string })
  | (Omit<TrueFalseQuestion, 'id'> & { id?: string })
  | (Omit<SlidingScaleQuestion, 'id'> & { id?: string });

export interface CreateOrganisationCampaignRequest {
  campaignName: string;
  title: string;
  goal: string;
  summary: string;
  maxSpend: number;
  coPayPerCompletion: number;
  questions: CampaignQuestionCreateInput[];
  filters?: CampaignFilters;
}

export type UpdateOrganisationCampaignRequest = CreateOrganisationCampaignRequest;

export interface CampaignLocationFilter {
  states: string[];
  postCodes: string[];
  radiusKm?: number | null;
}

export interface CampaignFilters {
  professionIds: string[];
  accountTypeIds: string[];
  organisationIds: string[];
  location?: CampaignLocationFilter | null;
  experienceYearsMin?: number | null;
  experienceYearsMax?: number | null;
  requiredTags: string[];
  anyTags: string[];
  excludeUserIds: string[];
  maxAudienceSize?: number | null;
}

export interface PrimaryProfessionOption {
  id: string;
  name: string;
  description: string;
  level: number;
}

export interface CampaignAnswer {
  questionId: string;
  textValue?: string | null;
  selectedOptions?: string[] | null;
  numericValue?: number | null;
  boolValue?: boolean | null;
}

export interface CampaignResponseSummary {
  id: string;
  campaignId: string;
  campaignTitle: string;
  userId: string;
  professionName: string;
  status: 'Started' | 'InComplete' | 'Complete' | 'Expired';
  startTime: string;
  lastModified: string;
  completedTime?: string | null;
  answerCount: number;
}

export interface CampaignResponseDetail {
  id: string;
  campaignId: string;
  organisationId?: string;
  campaignTitle?: string;
  userId: string;
  professionName?: string;
  answers: CampaignAnswer[];
  status: 'Started' | 'InComplete' | 'Complete' | 'Expired';
  startTime: string;
  lastModified: string;
  completedTime?: string | null;
  claraSessionId?: string;
}

export interface CampaignProgressResponse {
  id: string;
  campaignId: string;
  userId: string;
  answers: CampaignAnswer[];
  status: 'Started' | 'InComplete' | 'Complete' | 'Expired';
  startTime: string;
  lastModified: string;
  completedTime?: string | null;
}

export interface IncompleteCampaign {
  campaign: Campaign;
  response: CampaignProgressResponse;
  estimatedCompletionSeconds: number;
  nextQuestionIndex: number;
  nextQuestionId?: string | null;
}

/** Profile-facing follow-up row; no response id — answers stay anonymous on other screens. */
export interface CampaignFollowupContact {
  userId: string;
  campaignId: string;
  campaignTitle: string;
  displayName: string;
  workEmail?: string | null;
  workPhone?: string | null;
  professionName: string;
  occupation: string;
}

export interface CampaignFollowupContactsPage {
  items: CampaignFollowupContact[];
  page: number;
  pageSize: number;
  total: number;
}

export type CampaignFollowupSortField =
  | 'lastModified'
  | 'completedTime'
  | 'campaignTitle'
  | 'displayName'
  | 'professionName';

export type OrganisationDirectorySource = 'all' | 'consultation' | 'campaignFollowup';

export interface OrganisationDirectoryUser {
  userId: string;
  displayName: string;
  professionName: string;
  occupation: string;
  lastActivityAt?: string | null;
  hasConsultation: boolean;
  hasCampaignFollowup: boolean;
}

export interface OrganisationDirectoryUsersPage {
  items: OrganisationDirectoryUser[];
  page: number;
  pageSize: number;
  total: number;
}

export interface OrganisationUserHistoryConsultation {
  id: string;
  startTime: string;
  status: string;
  consultationType: string;
  summary: string;
}

export interface OrganisationUserHistoryCampaign {
  campaignId: string;
  title: string;
  completedTime?: string | null;
}

export interface OrganisationUserHistory {
  userId: string;
  displayName: string;
  professionName: string;
  occupation: string;
  bio: string;
  consultations: OrganisationUserHistoryConsultation[];
  campaignsWithFollowup: OrganisationUserHistoryCampaign[];
}

// ============================================================================
// Organisation Types
// ============================================================================

export enum OrganisationStatus {
  Active = 'Active',
  Unverified = 'Unverified',
  Approved = 'Approved',
  Disabled = 'Disabled',
  Deleted = 'Deleted',
}

export enum OrganisationRole {
  owner = 'owner',
  administrator = 'administrator',
  paymentAdministrator = 'paymentAdministrator',
  user = 'user',
}

export enum OrganisationMemberStatus {
  pending = 'pending',
  accepted = 'accepted',
  removed = 'removed',
  declined = 'declined',
}

export interface OrganisationMember {
  id: string;
  role: OrganisationRole;
  status: OrganisationMemberStatus;
  leadNotificationsEnabled: boolean;
}

export interface Organisation {
  id: string;
  type: string;
  status: OrganisationStatus;
  name: string;
  bio: string;
  abn: string;
  stripeAccountId: string;
  image?: S3Blob | null;
  bannerImage?: S3Blob | null;
  members: OrganisationMember[];
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  isFollowing?: boolean;
  followerCount?: number;
}

// ============================================================================
// News Feed Types
// ============================================================================

export type PostVisibility = 'public' | 'followers' | 'internal';

export interface NewsFeedPost {
  id: string;
  authorId: string;
  creationDateTime: string;
  title: string;
  subtitle: string;
  body: string;
  organisationId?: string | null;
  visibility: PostVisibility;
  postedAsOrganisation: boolean;
  /** Presigned URL; empty string when there is no (visible) attachment. */
  attachment?: string;
  attachmentContentType?: string | null;
  likeCount: number;
  commentCount: number;
  userLikedPost: boolean;
}

export interface NewsFeedPostsResponse {
  data: NewsFeedPost[];
  currentPage: number;
  hasMore: boolean;
  totalCount: number;
  pageSize: number;
  totalPages: number;
}

export interface NewsFeedUploadUrlResponse {
  key: string;
  uploadUrl: string;
}

// ============================================================================
// Connection Overview Types
// ============================================================================

export interface UserConnectionData {
  id: string;
  accountType: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bio: string;
  timezone: string;
  onboardingComplete: boolean;
  firstConsultCompleted: boolean;
  primaryProfession: string;
  memberOfOrganisation: string;
  answers: Record<string, string>;
}

export interface ConnectionOverviewResponse {
  questions: string[];
  users: UserConnectionData[];
}

// ============================================================================
// Organisation Payment Method Types
// ============================================================================

export interface StripePaymentCard {
  id: string;
  last4: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  funding: string;
  isValid: boolean;
  primaryMethod: boolean;
}

export interface OutstandingTransaction {
  consultationId: string;
  transactionStatus: TransactionStatuses;
  startTime: string; // ISO date string
  totalAmount: number;
}

export interface OrganisationPaymentMethod {
  stripePaymentCards: StripePaymentCard[];
  outstandingTotal: number;
  outstandingTransactions: OutstandingTransaction[];
}

export interface OrganisationPaymentIntentResponse {
  customer: string;
  ephemeralKeySecret: string;
  setupIntent: string;
}

export interface UpdateMemberOrganisationRequest {
  name?: string;
  bio?: string;
  email?: string;
  phone?: string;
  placeId?: string;
}

export type OrganisationCreditTransactionType = 'recharge' | 'allocation';
export type OrganisationCreditTransactionStatus = 'pending' | 'complete' | 'cancelled';

export interface OrganisationCreditWallet {
  id: string;
  organisationId: string;
  balance: number;
  allocated: number;
  available: number;
  updatedAt: string;
}

export interface OrganisationCreditTransaction {
  id: string;
  organisationId: string;
  type: OrganisationCreditTransactionType;
  status: OrganisationCreditTransactionStatus;
  amount: number;
  currency: string;
  description?: string | null;
  reference?: string | null;
  consultationId?: string | null;
  stripeTransactionId?: string | null;
  createdAt: string;
  estimatedClearingDate?: string | null;
}

export interface AddOrganisationCreditResponse {
  wallet: OrganisationCreditWallet;
  transaction: OrganisationCreditTransaction;
}

export interface OrganisationCreditTransactionsResponse {
  transactions: OrganisationCreditTransaction[];
  page: number;
  pageSize: number;
  total: number;
}

// ============================================================================
// STAT Signal
// ============================================================================

export type SignalFunnelStatus = 'Draft' | 'Active' | 'Paused' | 'Cancelled';
export type SignalFunnelMatchMode = 'AnyTopic' | 'AllTopics';
export type SignalFunnelTopicMatchStyle = 'Exact' | 'Related';
export type SignalFunnelReflectionWindowType = 'AllTime' | 'Last30Days' | 'Last60Days' | 'Last90Days' | 'Custom';
export type SignalFunnelTagSource = 'ClaraTag' | 'Snomed' | 'Taxonomy';
export type SignalMatchType = 'Reflection' | 'Lead';
export type SignalPendingMatchStatus = 'Pending' | 'Claimed' | 'Cancelled';
export type SignalClaimMode = 'All' | 'FirstN' | 'Quantity';

export interface SignalFunnelTagRule {
  label: string;
  minConfidence: number;
  source: SignalFunnelTagSource;
}

export type SignalFunnelNotificationFrequency = 'None' | 'Instant' | 'Daily' | 'Weekly';

export interface SignalFunnel {
  id: string;
  organisationId: string;
  name: string;
  description: string;
  status: SignalFunnelStatus;
  matchMode: SignalFunnelMatchMode;
  topicMatchStyle: SignalFunnelTopicMatchStyle;
  reflectionWindowType: SignalFunnelReflectionWindowType;
  reflectionWindowCustomFrom?: string;
  reflectionWindowCustomTo?: string;
  tags: SignalFunnelTagRule[];
  notifyOnNewMatches: boolean;
  memberNotificationFrequency: SignalFunnelNotificationFrequency;
  engagementSummary?: string | null;
  engagementSummaryGeneratedAt?: string | null;
  engagementSummaryProvider?: string | null;
  autoRegenerateEngagementSummary?: boolean;
  audienceFilterEnabled?: boolean;
  professionIds?: string[];
  autoClaimEnabled: boolean;
  autoClaimBudgetAmount: number;
  autoClaimBudgetConsumed: number;
  createdAt: string;
  updatedAt: string;
  matchingCriteriaUpdatedAt?: string | null;
}

export interface CreateSignalFunnelRequest {
  organisationId: string;
  name: string;
  description?: string;
  matchMode?: SignalFunnelMatchMode;
  topicMatchStyle?: SignalFunnelTopicMatchStyle;
  reflectionWindowType?: SignalFunnelReflectionWindowType;
  reflectionWindowCustomFrom?: string;
  reflectionWindowCustomTo?: string;
  tags: SignalFunnelTagRule[];
  notifyOnNewMatches?: boolean;
  memberNotificationFrequency?: SignalFunnelNotificationFrequency;
  autoClaimEnabled?: boolean;
  autoClaimBudgetAmount?: number;
  audienceFilterEnabled?: boolean;
  professionIds?: string[];
}

export interface UpdateSignalFunnelRequest {
  name: string;
  description?: string;
  matchMode?: SignalFunnelMatchMode;
  topicMatchStyle?: SignalFunnelTopicMatchStyle;
  reflectionWindowType?: SignalFunnelReflectionWindowType;
  reflectionWindowCustomFrom?: string;
  reflectionWindowCustomTo?: string;
  tags: SignalFunnelTagRule[];
  notifyOnNewMatches?: boolean;
  memberNotificationFrequency?: SignalFunnelNotificationFrequency;
  autoClaimEnabled?: boolean;
  autoClaimBudgetAmount?: number;
  audienceFilterEnabled?: boolean;
  professionIds?: string[];
  status?: SignalFunnelStatus;
  engagementSummary?: string | null;
  autoRegenerateEngagementSummary?: boolean;
}

export interface UpdateSignalFunnelMatchingCriteriaRequest {
  matchMode?: SignalFunnelMatchMode;
  topicMatchStyle?: SignalFunnelTopicMatchStyle;
  reflectionWindowType?: SignalFunnelReflectionWindowType;
  reflectionWindowCustomFrom?: string;
  reflectionWindowCustomTo?: string;
  tags: SignalFunnelTagRule[];
  audienceFilterEnabled?: boolean;
  professionIds?: string[];
}

export interface SignalFunnelMatchingCriteriaImpact {
  pendingReflectionsToRemove: number;
  pendingLeadsToRemove: number;
  pendingReflectionsRetained: number;
  pendingLeadsRetained: number;
  criteriaChanged: boolean;
  isWidening: boolean;
  isNarrowing: boolean;
  summary: string;
}

export interface UpdateSignalFunnelMatchingCriteriaResult {
  funnel: SignalFunnel;
  impact: SignalFunnelMatchingCriteriaImpact;
}

export interface SignalMatchedTag {
  label: string;
  confidence: number;
}

export interface SignalPendingMatch {
  id: string;
  signalFunnelId: string;
  signalFunnelName: string;
  matchType: SignalMatchType;
  matchedTags: SignalMatchedTag[];
  unitCost: number;
  status: SignalPendingMatchStatus;
  createdAt: string;
}

export interface SignalPendingMatchesSummary {
  reflectionCount: number;
  leadCount: number;
  minimumClaimPoolSize: number;
  reflectionPoolMeetsMinimum: boolean;
  leadPoolMeetsMinimum: boolean;
  reflectionCost: number;
  leadCost: number;
  totalCost: number;
  items: SignalPendingMatch[];
}

export interface SignalFunnelScanResult {
  signalFunnelId: string;
  newReflectionMatches: number;
  newLeadMatches: number;
  totalNewMatches: number;
  estimatedReflectionCost: number;
  estimatedLeadCost: number;
  estimatedTotalCost: number;
}

export interface ActivateSignalFunnelResponse {
  funnel: SignalFunnel;
  scan: SignalFunnelScanResult;
}

export interface ClaimSignalMatchesRequest {
  organisationId: string;
  signalFunnelId: string;
  mode: SignalClaimMode;
  quantity?: number;
}

export interface SignalReflectionQaItem {
  question: string;
  answer: string;
}

export interface SignalReflectionListItem {
  id: string;
  kind: 'Pending' | 'Claimed';
  signalFunnelId: string;
  signalFunnelName: string;
  occurredAt: string;
  unitCost?: number | null;
  activityTitle: string;
  previewSummary: string;
  matchedTagLabels: string[];
  insightStatus: SignalReflectionInsightStatus;
  matchedUnderPreviousCriteria?: boolean;
}

export type SignalReflectionInsightStatus = 'Preparing' | 'Available' | 'Unavailable';

export interface SignalClinicianProfessionHierarchy {
  primary?: string | null;
  secondary?: string | null;
  specialty?: string | null;
}

export interface SignalReflectionDetail {
  id: string;
  kind: 'Pending' | 'Claimed';
  signalFunnelId: string;
  signalFunnelName: string;
  claimedAt?: string | null;
  queuedAt?: string | null;
  unitCost?: number | null;
  activityTitle: string;
  sessionSummary?: string | null;
  reflectionSummary?: string | null;
  keyLearnings: string[];
  matchedTags: SignalMatchedTag[];
  reflectionTags: SignalMatchedTag[];
  reflectionItems: SignalReflectionQaItem[];
  deIdentified: boolean;
  insightStatus: SignalReflectionInsightStatus;
  clinicianProfessionHierarchy?: SignalClinicianProfessionHierarchy | null;
  sourceContext?: string | null;
  sourceContent?: string | null;
  keyInsights: string[];
  insightGeneratedAt?: string | null;
  insightPromptVersion?: string | null;
  matchedUnderPreviousCriteria?: boolean;
}

export interface SignalAnonymisedReflection {
  signalMatchId: string;
  signalFunnelId: string;
  signalFunnelName: string;
  claimedAt: string;
  sessionSummary?: string | null;
  reflectionSummary?: string | null;
  keyLearnings: string[];
  matchedTags: SignalMatchedTag[];
  reflectionTags: SignalMatchedTag[];
  matchedUnderPreviousCriteria?: boolean;
}

export interface SignalClaimedLead {
  signalMatchId: string;
  signalFunnelId: string;
  signalFunnelName: string;
  claimedAt: string;
  userId: string;
  displayName: string;
  professionName: string;
  occupation: string;
  division?: string | null;
  registrationType?: string | null;
  location?: string | null;
  matchedUnderPreviousCriteria?: boolean;
}

export interface ClaimSignalMatchesResult {
  claimedCount: number;
  totalCost: number;
  walletAvailableAfter: number;
  remainingPending: number;
  reflections: SignalAnonymisedReflection[];
  leads: SignalClaimedLead[];
}

export interface SignalMatch {
  id: string;
  signalFunnelId: string;
  signalFunnelName: string;
  matchType: SignalMatchType;
  unitCost: number;
  claimedAt: string;
  matchedTags: SignalMatchedTag[];
}

export type SignalAllocationStatus = 'Active' | 'Exhausted' | 'Cancelled';

export interface SignalAutoClaimSummary {
  allocationId?: string | null;
  matchType: SignalMatchType;
  enabled: boolean;
  allocatedAmount: number;
  consumedAmount: number;
  remainingAmount: number;
  status?: SignalAllocationStatus | null;
}

export interface SignalAutoClaimLedgerEntry {
  allocationId: string;
  matchType: SignalMatchType;
  createdAt: string;
  updatedAt: string;
  status: SignalAllocationStatus;
  allocatedAmount: number;
  consumedAmount: number;
  remainingAmount: number;
  refundedAmount: number;
}

export interface SignalFunnelAutoClaimAllocations {
  organisationId: string;
  signalFunnelId: string;
  signalFunnelName: string;
  signalFunnelStatus: SignalFunnelStatus;
  reflection: SignalAutoClaimSummary;
  lead: SignalAutoClaimSummary;
  reflectionLedger: SignalAutoClaimLedgerEntry[];
  leadLedger: SignalAutoClaimLedgerEntry[];
}

export interface ConfigureSignalAutoClaimAllocationRequest {
  enabled: boolean;
  budgetAmount: number;
}

export interface SignalSurfacingAllocation {
  id: string;
  organisationId: string;
  signalFunnelId: string;
  signalFunnelName: string;
  campaignId: string;
  campaignTitle: string;
  allocatedAmount: number;
  consumedAmount: number;
  remainingAmount: number;
  status: SignalAllocationStatus;
  createdAt: string;
}

export interface SignalMeetingAllocation {
  id: string;
  organisationId: string;
  signalFunnelId?: string | null;
  signalFunnelName?: string | null;
  allocatedAmount: number;
  consumedAmount: number;
  remainingAmount: number;
  status: SignalAllocationStatus;
  createdAt: string;
}

export interface CreateSignalSurfacingAllocationRequest {
  organisationId: string;
  signalFunnelId: string;
  campaignId: string;
  amount: number;
  reflectionWindowType?: SignalFunnelReflectionWindowType;
  reflectionWindowCustomFrom?: string;
  reflectionWindowCustomTo?: string;
}

export interface UpdateSignalSurfacingCampaignReflectionWindowRequest {
  organisationId: string;
  signalFunnelId: string;
  reflectionWindowType?: SignalFunnelReflectionWindowType;
  reflectionWindowCustomFrom?: string;
  reflectionWindowCustomTo?: string;
}

export interface CreateSignalMeetingAllocationRequest {
  organisationId: string;
  signalFunnelId: string;
  amount: number;
}

export interface SignalSurfacingCampaignAnalyticsRow {
  allocationId: string;
  campaignId: string;
  campaignTitle: string;
  campaignStatus: string;
  allocatedAmount: number;
  consumedAmount: number;
  remainingAmount: number;
  status: SignalAllocationStatus;
  completedResponses: number;
  partialResponses: number;
  campaignSpend: number;
  utilisationPercent: number;
}

export interface SignalOrganisationSurfacingAnalytics {
  totalAllocated: number;
  totalConsumed: number;
  totalRemaining: number;
  activeAllocationCount: number;
  totalCompletedResponses: number;
  campaigns: SignalSurfacingCampaignAnalyticsRow[];
}

export type SignalSurfacingCampaignSort =
  | 'LastAllocatedAtDesc'
  | 'CompletedResponsesDesc'
  | 'RemainingActiveDesc'
  | 'CampaignTitleAsc';

export type SignalSurfacingStateFilter =
  | 'All'
  | 'ActiveBudget'
  | 'ConsumedOnly'
  | 'CancelledRefunded'
  | 'NoActiveBudget';

export interface SignalSurfacingCampaignRow {
  signalFunnelId: string;
  signalFunnelName: string;
  campaignId: string;
  campaignTitle: string;
  campaignStatus: string;
  activeAllocatedAmount: number;
  consumedAmount: number;
  remainingActiveAmount: number;
  pausedReserveAmount: number;
  historicalAllocatedAmount: number;
  refundedAmount: number;
  activeAllocationCount: number;
  cancelledAllocationCount: number;
  exhaustedAllocationCount: number;
  startedResponseCount: number;
  completedResponseCount: number;
  partialResponseCount: number;
  claimedLeadCount: number;
  bookedConsultationCount: number;
  utilisationPercent: number;
  reflectionWindowType: SignalFunnelReflectionWindowType;
  reflectionWindowCustomFrom?: string | null;
  reflectionWindowCustomTo?: string | null;
  lastAllocatedAt?: string | null;
  lastCompletedAt?: string | null;
}

export interface SignalSurfacingCampaignsPage {
  items: SignalSurfacingCampaignRow[];
  page: number;
  pageSize: number;
  total: number;
  totalActiveAllocated: number;
  totalConsumed: number;
  totalRemainingActive: number;
  totalPausedReserve: number;
  totalHistoricalAllocated: number;
  totalRefunded: number;
  totalCompletedResponses: number;
  totalClaimedLeads: number;
  totalBookedConsultations: number;
}

export interface SignalSurfacingAllocationLedger {
  allocationId: string;
  signalFunnelId: string;
  createdAt: string;
  status: SignalAllocationStatus;
  allocatedAmount: number;
  consumedAmount: number;
  remainingAmount: number;
  refundedAmount: number;
}

export interface SignalSurfacingCampaignDetail {
  summary: SignalSurfacingCampaignRow;
  criteriaSummary: string[];
  allocations: SignalSurfacingAllocationLedger[];
  attributionNotes: string[];
}

export interface TopUpSignalSurfacingAllocationRequest {
  organisationId: string;
  amount: number;
}

export type SignalTrackAttentionState = 'Healthy' | 'NeedsAttention' | 'SetupRequired' | 'Paused' | 'Cancelled';
export type SignalTrackInsightState = 'Suppressed' | 'Deterministic' | 'Preparing' | 'Available' | 'Unavailable';

export interface SignalMonthlyCount {
  month: string;
  count: number;
}

export interface SignalProfessionDistribution {
  profession: string;
  count: number;
  percentage: number;
}

export interface SignalTrackInsight {
  state: SignalTrackInsightState;
  claimedSampleSize: number;
  headline?: string | null;
  story?: string | null;
  keyInsights: string[];
  barrierCodes: string[];
  recommendedAction?: string | null;
  generatedAt?: string | null;
}

export interface SignalTrackMovement {
  currentWeekNewMatches: number;
  priorWeekNewMatches: number;
  newMatchDelta: number;
  currentWeekClaims: number;
  priorWeekClaims: number;
  claimDelta: number;
  summary: string;
}

export interface SignalTrackMatchMotion {
  pendingCount: number;
  claimedCount: number;
  pendingCost: number;
  minimumPoolSize: number;
  poolMeetsMinimum: boolean;
  autoClaim: SignalAutoClaimSummary;
}

export interface SignalTrackMeetingsMotion {
  configured: boolean;
  activeAllocated: number;
  consumed: number;
  remaining: number;
  interests: number;
}

export interface SignalTrackCampaignsMotion {
  configured: boolean;
  campaignCount: number;
  activeCount: number;
  pausedCount: number;
  activeAllocated: number;
  consumed: number;
  remaining: number;
  pausedReserve: number;
  completedResponses: number;
}

export interface SignalTrackConfigurationSummary {
  matchMode: string;
  topicMatchStyle: string;
  reflectionWindow: string;
  topics: string[];
  audienceFilterEnabled: boolean;
  audienceProfessionCount: number;
}

export interface SignalTrackOverview {
  trackId: string;
  organisationId: string;
  name: string;
  description: string;
  status: SignalFunnelStatus;
  configuration: SignalTrackConfigurationSummary;
  movement: SignalTrackMovement;
  claimedReflectionCount: number;
  analyticsSuppressed: boolean;
  claimedReflectionSeries: SignalMonthlyCount[];
  professionDistribution: SignalProfessionDistribution[];
  reflections: SignalTrackMatchMotion;
  leads: SignalTrackMatchMotion;
  meetings: SignalTrackMeetingsMotion;
  campaigns: SignalTrackCampaignsMotion;
  attentionState: SignalTrackAttentionState;
  headline: string;
  nextActions: string[];
  insight: SignalTrackInsight;
}

export interface SignalTrackBriefing {
  trackId: string;
  name: string;
  status: SignalFunnelStatus;
  claimedReflectionCount: number;
  analyticsSuppressed: boolean;
  claimedReflectionSeries: SignalMonthlyCount[];
  attentionState: SignalTrackAttentionState;
  headline: string;
  nextActions: string[];
  insight: SignalTrackInsight;
}

export interface SignalCrossTrackPattern {
  barrierCode: string;
  trackCount: number;
  trackIds: string[];
  summary: string;
}

export interface SignalTrackAttentionItem {
  trackId: string;
  name: string;
  state: SignalTrackAttentionState;
  headline: string;
  nextActions: string[];
}

export interface SignalPulseOverview {
  organisationId: string;
  walletAvailable: number;
  activeTrackCount: number;
  totalTrackCount: number;
  newReflectionsThisMonth: number;
  newReflectionsPreviousMonth: number;
  newReflectionsDelta: number;
  practitionersReached: number;
  tracksNeedingAttentionCount: number;
  tracksNeedingAttention: SignalTrackAttentionItem[];
  tracks: SignalTrackBriefing[];
  nextActions: string[];
  crossTrackPattern?: SignalCrossTrackPattern | null;
}

export interface RegenerateSignalTrackInsightRequest {
  organisationId: string;
  periodStart?: string;
}

export interface RegenerateSignalTrackInsightResponse {
  id: string;
  trackId: string;
  periodStart: string;
  periodEnd: string;
  claimedSampleSize: number;
  status: 'Preparing' | 'Available' | 'Unavailable';
  generatedAt?: string | null;
  provider?: string | null;
  modelVersion?: string | null;
  promptVersion: string;
  sourceHash: string;
  failureReason?: string | null;
}

export interface ListSignalSurfacingCampaignsParams {
  q?: string;
  campaignStatus?: string;
  surfacingState?: SignalSurfacingStateFilter;
  dateFrom?: string;
  dateTo?: string;
  sort?: SignalSurfacingCampaignSort;
  page?: number;
  pageSize?: number;
}

export interface SignalConsultationInterestedUser {
  signalMatchId?: string | null;
  engagementInterestId?: string | null;
  userId: string;
  displayName: string;
  professionName: string;
  occupation: string;
  division?: string | null;
  registrationType?: string | null;
  location?: string | null;
  interestedAt?: string;
  claimedAt: string;
  unitCost?: number;
}

export type SignalConsultationFunnelSort =
  | 'LastAllocatedAtDesc'
  | 'ClaimedLeadsDesc'
  | 'PendingLeadsDesc'
  | 'RemainingBudgetDesc'
  | 'EngagementInterestsDesc'
  | 'FunnelNameAsc';

export type SignalMeetingBudgetStateFilter =
  | 'All'
  | 'ActiveBudget'
  | 'ConsumedOnly'
  | 'CancelledRefunded'
  | 'NoActiveBudget';

export interface SignalConsultationFunnelRow {
  signalFunnelId: string;
  signalFunnelName: string;
  funnelStatus: string;
  activeAllocatedAmount: number;
  consumedAmount: number;
  remainingActiveAmount: number;
  historicalAllocatedAmount: number;
  refundedAmount: number;
  activeAllocationCount: number;
  cancelledAllocationCount: number;
  exhaustedAllocationCount: number;
  claimedLeadsCount: number;
  pendingLeadsCount: number;
  bookedConsultationCount: number;
  engagementInterestsCount: number;
  engagementBudgetConsumed: number;
  lastEngagementInterestAt?: string | null;
  lastAllocatedAt?: string | null;
  lastClaimedAt?: string | null;
}

export interface SignalConsultationFunnelsPage {
  items: SignalConsultationFunnelRow[];
  page: number;
  pageSize: number;
  total: number;
  totalActiveAllocated: number;
  totalConsumed: number;
  totalRemainingActive: number;
  totalHistoricalAllocated: number;
  totalRefunded: number;
  totalClaimedLeads: number;
  totalPendingLeads: number;
  totalBookedConsultations: number;
  totalEngagementInterests: number;
  totalEngagementBudgetConsumed: number;
}

export interface SignalMeetingAllocationLedger {
  allocationId: string;
  createdAt: string;
  status: SignalAllocationStatus;
  allocatedAmount: number;
  consumedAmount: number;
  remainingAmount: number;
  refundedAmount: number;
}

export interface SignalConsultationFunnelDetail {
  summary: SignalConsultationFunnelRow;
  criteriaSummary: string[];
  allocations: SignalMeetingAllocationLedger[];
  attributionNotes: string[];
}

export interface SignalConsultationInterestedUsersPage {
  items: SignalConsultationInterestedUser[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ListSignalConsultationFunnelsParams {
  q?: string;
  funnelStatus?: string;
  budgetState?: SignalMeetingBudgetStateFilter;
  dateFrom?: string;
  dateTo?: string;
  sort?: SignalConsultationFunnelSort;
  page?: number;
  pageSize?: number;
}

export interface ListSignalConsultationInterestedUsersParams {
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface SignalFunnelConsultationAnalyticsRow {
  signalFunnelId: string;
  signalFunnelName: string;
  funnelStatus: string;
  meetingBudgetAllocated: number;
  meetingBudgetConsumed: number;
  meetingBudgetRemaining: number;
  claimedLeadsCount: number;
  pendingLeadsCount: number;
  engagementInterestsCount: number;
  engagementBudgetConsumed: number;
  interestedUsers: SignalConsultationInterestedUser[];
}

export interface SignalOrganisationConsultationAnalytics {
  totalMeetingBudgetAllocated: number;
  totalMeetingBudgetConsumed: number;
  totalMeetingBudgetRemaining: number;
  totalClaimedLeads: number;
  totalPendingLeads: number;
  totalEngagementInterests: number;
  totalEngagementBudgetConsumed: number;
  funnels: SignalFunnelConsultationAnalyticsRow[];
}

export interface SignalOrgAnalyticsRow {
  organisationId: string;
  organisationName: string;
  activeFunnels: number;
  pendingReflections: number;
  pendingLeads: number;
  pendingCost: number;
  claimedReflections: number;
  claimedLeads: number;
  claimedRevenue: number;
}

export interface SignalFunnelMatchSummary {
  pendingReflectionCount: number;
  pendingLeadCount: number;
  pendingReflectionCost: number;
  pendingLeadCost: number;
  claimedReflectionCount: number;
  claimedLeadCount: number;
}

export interface SignalFunnelStaffDetail {
  funnel: SignalFunnel;
  organisationName: string;
  organisationWalletAvailable: number;
  matchSummary: SignalFunnelMatchSummary;
}

export interface SignalFunnelAnalyticsRow {
  funnelId: string;
  funnelName: string;
  organisationId: string;
  organisationName: string;
  status: SignalFunnelStatus;
  matchMode: SignalFunnelMatchMode;
  topicCount: number;
  audienceFilterEnabled?: boolean;
  audienceProfessionCount?: number;
  pendingReflections: number;
  pendingLeads: number;
  pendingCost: number;
  claimedReflections: number;
  claimedLeads: number;
  claimedRevenue: number;
}

export interface SignalAnalyticsListPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export type SignalPlatformOverviewOrgSort =
  | 'RecentActivityDesc'
  | 'OrganisationNameAsc'
  | 'PendingCostDesc'
  | 'SurfacingRemainingDesc'
  | 'MeetingRemainingDesc'
  | 'TotalBudgetRemainingDesc'
  | 'BookedConsultationsDesc'
  | 'ClaimedRevenueDesc';

export type SignalPlatformMatchingOrgSort =
  | 'PendingCostDesc'
  | 'OrganisationNameAsc'
  | 'ActiveFunnelsDesc'
  | 'PendingReflectionsDesc'
  | 'ClaimedRevenueDesc';

export type SignalPlatformMatchingFunnelSort =
  | 'PendingCostDesc'
  | 'FunnelNameAsc'
  | 'OrganisationNameAsc'
  | 'PendingReflectionsDesc'
  | 'ClaimedRevenueDesc';

export type SignalPlatformSurfacingOrgSort =
  | 'RemainingActiveDesc'
  | 'OrganisationNameAsc'
  | 'CampaignCountDesc'
  | 'ConsumedDesc'
  | 'CompletedResponsesDesc'
  | 'ClaimedLeadsDesc'
  | 'BookedConsultationsDesc';

export type SignalPlatformSurfacingCampaignSort =
  | 'RemainingActiveDesc'
  | 'CampaignTitleAsc'
  | 'OrganisationNameAsc'
  | 'ConsumedDesc'
  | 'CompletedResponsesDesc'
  | 'UtilisationDesc';

export type SignalPlatformConsultationOrgSort =
  | 'ClaimedLeadsDesc'
  | 'OrganisationNameAsc'
  | 'RemainingActiveDesc'
  | 'PendingLeadsDesc'
  | 'BookedConsultationsDesc'
  | 'EngagementInterestsDesc';

export type SignalPlatformConsultationFunnelSort =
  | 'ClaimedLeadsDesc'
  | 'FunnelNameAsc'
  | 'OrganisationNameAsc'
  | 'PendingLeadsDesc'
  | 'RemainingActiveDesc'
  | 'BookedConsultationsDesc'
  | 'EngagementInterestsDesc';

export interface SignalPlatformOverviewSummary {
  from: string;
  to: string;
  activeFunnelCount: number;
  totalPendingCost: number;
  totalSurfacingRemainingActive: number;
  totalMeetingRemainingActive: number;
  campaignCountWithSignalBudget: number;
  consultationFunnelCount: number;
  claimedLeadsInPeriod: number;
  totalBookedConsultations: number;
}

export interface SignalPlatformOverviewOrgRow {
  organisationId: string;
  organisationName: string;
  pendingCost: number;
  surfacingRemaining: number;
  meetingRemaining: number;
  bookedConsultations: number;
  claimedRevenue: number;
  lastActivityAt?: string | null;
}

export interface SignalPlatformMatchingSummary {
  from: string;
  to: string;
  activeFunnelCount: number;
  totalPendingReflections: number;
  totalPendingLeads: number;
  totalPendingCost: number;
  claimedReflectionsInPeriod: number;
  claimedLeadsInPeriod: number;
  claimedRevenueInPeriod: number;
}

export interface SignalPlatformSurfacingSummary {
  from: string;
  to: string;
  campaignCount: number;
  activeAllocationCount: number;
  totalActiveAllocated: number;
  totalConsumed: number;
  totalRemainingActive: number;
  totalCompletedResponses: number;
  totalClaimedLeads: number;
  totalBookedConsultations: number;
  completedResponsesInPeriod: number;
  bookedConsultationsInPeriod: number;
}

export interface SignalPlatformConsultationSummary {
  from: string;
  to: string;
  funnelCount: number;
  totalActiveAllocated: number;
  totalConsumed: number;
  totalRemainingActive: number;
  totalPendingLeads: number;
  totalClaimedLeads: number;
  totalBookedConsultations: number;
  claimedLeadsInPeriod: number;
  bookedConsultationsInPeriod: number;
  totalEngagementInterests: number;
  totalEngagementBudgetConsumed: number;
  engagementInterestsInPeriod: number;
  engagementBudgetConsumedInPeriod: number;
}

export interface SignalListQueryParams {
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface SignalPlatformAnalytics {
  from: string;
  to: string;
  activeFunnelCount: number;
  totalPendingReflections: number;
  totalPendingLeads: number;
  totalPendingCost: number;
  claimedReflectionsInPeriod: number;
  claimedLeadsInPeriod: number;
  claimedRevenueInPeriod: number;
  byOrganisation: SignalOrgAnalyticsRow[];
  byFunnel: SignalFunnelAnalyticsRow[];
}

export interface SignalPlatformSurfacingOrgRow {
  organisationId: string;
  organisationName: string;
  campaignCount: number;
  activeAllocatedAmount: number;
  consumedAmount: number;
  remainingActiveAmount: number;
  completedResponses: number;
  claimedLeads: number;
  bookedConsultations: number;
}

export interface SignalPlatformSurfacingCampaignRow {
  organisationId: string;
  organisationName: string;
  campaignId: string;
  campaignTitle: string;
  campaignStatus: string;
  activeAllocatedAmount: number;
  consumedAmount: number;
  remainingActiveAmount: number;
  activeAllocationCount: number;
  completedResponseCount: number;
  partialResponseCount: number;
  claimedLeadCount: number;
  bookedConsultationCount: number;
  utilisationPercent: number;
}

export interface SignalPlatformSurfacingAnalytics {
  from: string;
  to: string;
  campaignCount: number;
  activeAllocationCount: number;
  totalActiveAllocated: number;
  totalConsumed: number;
  totalRemainingActive: number;
  totalCompletedResponses: number;
  totalClaimedLeads: number;
  totalBookedConsultations: number;
  completedResponsesInPeriod: number;
  bookedConsultationsInPeriod: number;
  byOrganisation: SignalPlatformSurfacingOrgRow[];
  byCampaign: SignalPlatformSurfacingCampaignRow[];
}

export interface SignalPlatformConsultationOrgRow {
  organisationId: string;
  organisationName: string;
  funnelCount: number;
  activeAllocatedAmount: number;
  consumedAmount: number;
  remainingActiveAmount: number;
  pendingLeads: number;
  claimedLeads: number;
  bookedConsultations: number;
  engagementInterests: number;
  engagementBudgetConsumed: number;
}

export interface SignalPlatformConsultationFunnelRow {
  organisationId: string;
  organisationName: string;
  signalFunnelId: string;
  signalFunnelName: string;
  funnelStatus: string;
  activeAllocatedAmount: number;
  consumedAmount: number;
  remainingActiveAmount: number;
  pendingLeadsCount: number;
  claimedLeadsCount: number;
  bookedConsultationCount: number;
  claimedLeadsInPeriod: number;
  bookedConsultationsInPeriod: number;
  engagementInterestsCount: number;
  engagementBudgetConsumed: number;
  engagementInterestsInPeriod: number;
  engagementBudgetConsumedInPeriod: number;
  lastEngagementInterestAt?: string;
  lastAllocatedAt?: string;
  lastClaimedAt?: string;
}

export interface SignalPlatformConsultationAnalytics {
  from: string;
  to: string;
  funnelCount: number;
  totalActiveAllocated: number;
  totalConsumed: number;
  totalRemainingActive: number;
  totalPendingLeads: number;
  totalClaimedLeads: number;
  totalBookedConsultations: number;
  claimedLeadsInPeriod: number;
  bookedConsultationsInPeriod: number;
  totalEngagementInterests: number;
  totalEngagementBudgetConsumed: number;
  engagementInterestsInPeriod: number;
  engagementBudgetConsumedInPeriod: number;
  byOrganisation: SignalPlatformConsultationOrgRow[];
  byFunnel: SignalPlatformConsultationFunnelRow[];
}

export const SIGNAL_MAX_FUNNEL_TAGS = 40;
export const SIGNAL_TAGS_PER_GENERATION_BATCH = 10;

export interface GenerateSignalFunnelTagsRequest {
  organisationId: string;
  name: string;
  description: string;
  existingTags: SignalFunnelTagRule[];
}

export interface GenerateSignalFunnelTagsResponse {
  tags: SignalFunnelTagRule[];
  totalTagCount: number;
  atMaxTags: boolean;
  llmProvider: string;
  llmModelId: string;
}

export interface SignalManagementSettings {
  funnelTagGenerationSystemPrompt: string;
  engagementInterestCostAud: number;
  funnelEngagementSummarySystemPrompt: string;
  reflectionInsightSystemPrompt: string;
  trackInsightSystemPrompt: string;
}

export interface UpsertSignalManagementSettingsRequest {
  funnelTagGenerationSystemPrompt: string;
  engagementInterestCostAud: number;
  funnelEngagementSummarySystemPrompt: string;
  reflectionInsightSystemPrompt: string;
  trackInsightSystemPrompt: string;
}

export interface SignalEngagementOpportunity {
  signalFunnelId: string;
  organisationId: string;
  organisationName: string;
  summary: string;
  organisationImageUrl?: string | null;
  matchedTagLabels: string[];
}

export interface SignalEngagementOpportunitiesResponse {
  claraSessionId?: string | null;
  campaignResponseId?: string | null;
  items: SignalEngagementOpportunity[];
}

export interface ExpressSignalEngagementInterestRequest {
  signalFunnelId: string;
  claraSessionId: string;
  campaignResponseId?: string;
}

export interface ExpressSignalEngagementInterestResponse {
  engagementInterestId: string;
}

// ============================================================================
// Matching Interests Types
// ============================================================================

export interface InterestedUser {
  id: string;
  fullName: string;
  profession: string;
  inquired: string; // ISO date string
  lastFollowup?: string | null; // ISO date string or null
}

export interface OrganisationWithInterests {
  id: string;
  name: string;
  bio: string;
  status: OrganisationStatus;
  interestCount: number;
  interestedUsers: InterestedUser[];
}

// ============================================================================
// Matching Answers Types
// ============================================================================

export interface MatchingAnswersResponse {
  id: string;
  fullName: string;
  profession: string;
  status: 'NOT STARTED' | 'PARTIAL' | 'COMPLETE';
  answeredCount?: number;
  totalQuestions?: number;
  updated?: string; // ISO date string
  professionType: 'HCP' | 'Industry' | 'General User';
  userMatchmakingAnswers?: {
    id: string;
    answers: Array<{
      id: string;
      textValue?: string;
      selectedOptions?: string[];
      sliderValue?: number;
      notes?: string;
      answeredAt: string;
    }>;
    createdAt: string;
    updatedAt: string;
    isCompleted: boolean;
  };
}

/**
 * Current user's STAT CoPay™ balances, as returned by POST /api/transaction/fetch-history.
 * Two separate balances that are never added together: the Wallet Balance (the practitioner's
 * own earnings) and the Giving Balance (funded by STAT and directed by the practitioner; not
 * their income). Each has a pending amount alongside.
 */
export interface UserCoPayWallet {
  /** Settled CoPay ("Giving") balance in AUD. */
  coPayBalance: number;
  /** Settled market-research ("Wallet") balance in AUD. */
  marketResearchBalance: number;
  /** CoPay earned but not yet settled. */
  copayPending: number;
  /** Market-research earnings not yet settled. */
  marketResearchPending: number;
}
